<template>
  <div class="filter-panel-wrapper">
    <v-expand-transition>
      <div
        v-if="show"
        :class="{ 'filter-panel': true, 'filter-panel--mobile': smAndDown }"
        data-product-fruits="ad-materials-filter-panel"
      >
        <div class="filter-header">
          <h3>{{ $t('ad-materials.filter.title') }} ({{ materialsAmount }})</h3>
          <v-btn
            icon
            variant="text"
            @click="closePanel"
            density="comfortable"
          >
            <v-icon class="text-grey-darken-2">mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="filter-content">
          <!-- Target Audience -->
          <v-badge
            :model-value="activeFilters.target !== 'all'"
            class="w-full"
            color="error"
            content="1"
          >
            <div class="filter-section">
              <div
                class="section-header"
                @click="toggleSection('target')"
              >
                <h4>{{ $t('ad-materials.filter.target.label') }}</h4>
                <v-icon>{{
                  expandedSections.target ? 'mdi-chevron-up' : 'mdi-chevron-down'
                }}</v-icon>
              </div>
              <v-expand-transition>
                <div v-show="expandedSections.target">
                  <div class="checkbox-group">
                    <v-radio-group v-model="selectedTargets">
                      <v-radio
                        v-for="(label, value) in targetOptions"
                        :key="value"
                        color="primary"
                        :label="label"
                        :value="value"
                        density="compact"
                      />
                    </v-radio-group>
                  </div>
                </div>
              </v-expand-transition>
            </div>
          </v-badge>

          <!-- Topic -->
          <v-badge
            :model-value="!!selectedTopics.length"
            class="w-full"
            color="error"
            :content="selectedTopics.length"
          >
            <div
              class="filter-section"
              v-if="topics.length"
            >
              <div
                class="section-header"
                @click="toggleSection('topic')"
              >
                <h4>{{ $t('ad-materials.filter.topic.label') }}</h4>

                <v-icon>{{
                  expandedSections.topic ? 'mdi-chevron-up' : 'mdi-chevron-down'
                }}</v-icon>
              </div>
              <v-expand-transition>
                <div
                  v-show="expandedSections.topic"
                  class="checkbox-group"
                >
                  <v-checkbox
                    v-for="topic in translatedTopics"
                    :key="topic.id"
                    v-model="selectedTopics"
                    color="primary"
                    :value="topic.id"
                    density="compact"
                  >
                    <template v-slot:label>
                      <span class="filter-label">
                        {{ topic.name }}
                        <span
                          v-if="topic.count !== undefined"
                          class="count-badge"
                          >({{ topic.count }})</span
                        >
                      </span>
                    </template>
                  </v-checkbox>
                </div>
              </v-expand-transition>
            </div>
          </v-badge>
          <!-- Asset Type -->
          <v-badge
            :model-value="!!selectedAssetTypes.length"
            class="w-full"
            color="error"
            :content="selectedAssetTypes.length"
          >
            <div class="filter-section">
              <div
                class="section-header"
                @click="toggleSection('assetType')"
              >
                <h4>{{ $t('ad-materials.filter.asset-type.label') }}</h4>
                <v-icon>{{
                  expandedSections.assetType ? 'mdi-chevron-up' : 'mdi-chevron-down'
                }}</v-icon>
              </div>
              <v-expand-transition>
                <div v-show="expandedSections.assetType">
                  <v-checkbox
                    v-for="assetType in props.assetTypes"
                    v-model="selectedAssetTypes"
                    :key="assetType.type"
                    color="primary"
                    :value="assetType.type"
                    density="compact"
                  >
                    <template v-slot:label>
                      <span class="filter-label">
                        {{ getAssetTypeLabel(assetType.type) }}
                        <span
                          v-if="assetType.count !== undefined"
                          class="count-badge"
                          >({{ assetType.count }})</span
                        >
                      </span>
                    </template>
                  </v-checkbox>
                </div>
              </v-expand-transition>
            </div>
          </v-badge>

          <!-- Origin -->
          <v-badge
            :model-value="!!selectedOrigins.length"
            class="w-full"
            color="error"
            :content="selectedOrigins.length"
          >
            <div class="filter-section">
              <div
                class="section-header"
                @click="toggleSection('origin')"
              >
                <h4>{{ $t('ad-materials.filter.origin.label') }}</h4>
                <v-icon>{{
                  expandedSections.origin ? 'mdi-chevron-up' : 'mdi-chevron-down'
                }}</v-icon>
              </div>
              <v-expand-transition>
                <div v-show="expandedSections.origin">
                  <v-checkbox
                    v-for="imageSource in props.imageSources"
                    v-model="selectedOrigins"
                    :key="imageSource.source"
                    color="primary"
                    :value="imageSource.source"
                    density="compact"
                  >
                    <template v-slot:label>
                      <span class="filter-label">
                        {{ getOriginTypeLabel(imageSource.source) }}
                        <span
                          v-if="imageSource.count !== undefined"
                          class="count-badge"
                          >({{ imageSource.count }})</span
                        >
                      </span>
                    </template>
                  </v-checkbox>
                </div>
              </v-expand-transition>
            </div>
          </v-badge>
        </div>

        <div class="filter-footer">
          <p class="info-text">{{ $t('ad-materials.filter.footer') }}</p>
          <v-btn
            block
            color="primary"
            variant="outlined"
            @click="resetFilters"
          >
            {{ $t('ad-materials.filter.reset') }}
          </v-btn>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'

import { useBranding } from '@/composables/useBranding'
import { useTopicTranslation } from '@/composables/useTopicTranslation'
import {
  AD_MATERIALS_EXPANDED_SECTIONS_KEY,
  DEFAULT_EXPANDED_SECTIONS,
  type ExpandedSections,
  loadFromLocalStorage,
  saveToLocalStorage,
} from '@/modules/adMaterials/helpers/adMaterialsFilterStorage'

import { getAssetTypeLabel as getAssetTypeLabelHelper } from './assetTypes'

const { t } = useI18n()

// Get topic translation helpers
const { translateTopics } = useTopicTranslation()

interface Filters {
  target: 'all' | 'buyers' | 'sellers'
  topics: string[]
  origins: string[]
  search: string
  sort: string
  assetTypes: string[]
}

interface Topic {
  id: string | number
  name: string
  count?: number
}

interface AssetTypeFilter {
  type: string
  count: number
}

interface ImageSourceFilter {
  source: string
  count: number
}

const props = withDefaults(
  defineProps<{
    show: boolean
    topics: Topic[]
    materialsAmount?: number
    activeFilters: Filters
    assetTypes?: AssetTypeFilter[]
    imageSources?: ImageSourceFilter[]
  }>(),
  {
    show: false,
    materialsAmount: 0,
    topics: () => [],
    assetTypes: () => [],
    imageSources: () => [],
  }
)

const emit = defineEmits(['update:show', 'filter-change', 'filter-reset'])

const { smAndDown } = useDisplay()

const selectedTargets = ref<Filters['target']>('all')
const selectedTopics = ref<Filters['topics']>([])
const selectedOrigins = ref<Filters['origins']>([])
const selectedAssetTypes = ref<Filters['assetTypes']>([])

const expandedSections = ref<ExpandedSections>(
  loadFromLocalStorage<ExpandedSections>(AD_MATERIALS_EXPANDED_SECTIONS_KEY, {
    ...DEFAULT_EXPANDED_SECTIONS,
  })
)

const targetOptions = {
  buyers: t('ad-materials.filter.target.options.buyers'),
  sellers: t('ad-materials.filter.target.options.sellers'),
  all: t('ad-materials.filter.target.options.show-all'),
}

// Use the shared helper for asset type labels
const getAssetTypeLabel = (type: string): string => {
  return getAssetTypeLabelHelper(type, t)
}

const getOriginTypeLabel = (source: string): string => {
  return t(`content-creator.image-sources.${source}`, {
    platform: useBranding().readablePlatformName.value,
  })
}

// Translate topic names for display using composable
const translatedTopics = translateTopics(() => props.topics)

const toggleSection = (section: keyof typeof expandedSections.value) => {
  expandedSections.value[section] = !expandedSections.value[section]
}

const closePanel = () => {
  emit('update:show', false)
}

const resetFilters = () => {
  selectedTargets.value = 'all'
  selectedTopics.value = []
  selectedOrigins.value = []
  selectedAssetTypes.value = []
  emit('filter-reset')
  emitFilterChange()
}

const emitFilterChange = () => {
  emit('filter-change', {
    target: selectedTargets.value,
    topics: selectedTopics.value,
    origins: selectedOrigins.value,
    assetTypes: selectedAssetTypes.value,
  })
}

const initFilters = () => {
  selectedTargets.value = props.activeFilters?.target || 'all'
  selectedTopics.value = props.activeFilters?.topics || []
  selectedOrigins.value = props.activeFilters?.origins || []
  selectedAssetTypes.value = props.activeFilters?.assetTypes || []
}

onMounted(() => {
  initFilters()
})

watch(
  expandedSections,
  (sections) => {
    saveToLocalStorage(AD_MATERIALS_EXPANDED_SECTIONS_KEY, sections)
  },
  { deep: true }
)

watch([selectedTargets, selectedTopics, selectedOrigins, selectedAssetTypes], () => {
  emitFilterChange()
})
</script>

<style scoped>
.filter-panel-wrapper {
  width: 100%;
  max-width: 300px;
  flex-shrink: 0;
}

.filter-panel {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 1px 0 8px rgba(0, 0, 0, 0.1);
  height: calc(100% - 170px);
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-right: 0;
  overflow: hidden;
}
.filter-panel--mobile {
  height: 100%;
}

.filter-header {
  padding: 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  padding: 16px;
}

.filter-section {
  margin-bottom: 16px;
  width: 100%;
  border: 1px solid rgb(var(--v-theme-on-surface-variant));
  border-radius: 8px;
  padding: 4px 8px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 8px 0;
}

.section-header h4 {
  margin: 0;
  font-weight: 500;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 8px;
}

.filter-footer {
  padding: 16px;
  border-top: 1px solid #eee;
}

.info-text {
  font-size: 12px;
  color: #666;
  margin-bottom: 16px;
}

@media (max-width: 600px) {
  .filter-panel-wrapper {
    width: 100%;
  }
}

.v-expand-transition-enter-active,
.v-expand-transition-leave-active {
  transition: all 0.3s ease;
}

.v-expand-transition-enter-from,
.v-expand-transition-leave-to {
  opacity: 0;
  width: 0;
}

:deep(.v-input__details) {
  display: none !important;
}

.filter-label {
  display: inline-flex;
  align-items: center;
  width: 100%;
  flex-wrap: nowrap;
}

.count-badge {
  color: #666;
  font-size: 0.875rem;
  font-weight: normal;
  margin-left: 4px;
  white-space: nowrap;
  flex-shrink: 0;
}
</style>
